/**
 * @fileoverview Creator Dashboard React Hooks
 * Provides React hooks for interacting with the Creator Dashboard API
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  UserProfileResponse,
  CreatorProfileResponse,
  ContentPlanResponse,
  ContentRecommendationResponse,
  CreateUserProfileRequest,
  CreateCreatorProfileRequest,
  UpdateRecommendationStatusRequest,
} from '../types/shared';
import { createCreatorDashboardApiClient, CreatorDashboardApiClient } from '../api/creator-dashboard-client';
import { useAuth } from '../contexts/AuthContext';

// ============================================================================
// Configuration and Setup
// ============================================================================

import { API_BASE_URL } from '../config/environment';

/**
 * Hook to get configured API client instance
 */
export function useCreatorDashboardApiClient(): CreatorDashboardApiClient {
  const { user, signOut } = useAuth();

  return useMemo(() => {
    return createCreatorDashboardApiClient({
      baseUrl: API_BASE_URL,
      getAuthToken: async () => {
        // Get the JWT token from Cognito using AWS Amplify
        if (!user) {
          console.log('No user available for auth token');
          return null;
        }

        try {
          const session = await fetchAuthSession();
          // API Gateway Cognito authorizers expect ID tokens, not access tokens
          const idToken = session.tokens?.idToken?.toString();

          if (!idToken) {
            console.error('No ID token available in session');
            return null;
          }

          console.log('ID token retrieved successfully');
          return idToken;
        } catch (error) {
          console.error('Failed to get auth token:', error);
          return null;
        }
      },
      onAuthError: () => {
        // Sign out user on authentication errors
        signOut().catch(console.error);
      },
      retryAttempts: 3,
      retryDelay: 1000,
      cacheTimeout: 5 * 60 * 1000, // 5 minutes
    });
  }, [user, signOut]);
}

// ============================================================================
// User Profile Hooks
// ============================================================================

export interface UseUserProfileReturn {
  userProfile: UserProfileResponse | null;
  loading: boolean;
  error: string | null;
  createProfile: (profile: CreateUserProfileRequest) => Promise<void>;
  refreshProfile: () => Promise<void>;
}

/**
 * Hook for managing user profile data
 */
export function useUserProfile(): UseUserProfileReturn {
  const [userProfile, setUserProfile] = useState<UserProfileResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const apiClient = useCreatorDashboardApiClient();
  const { user, isAuthenticated } = useAuth();

  const fetchUserProfile = useCallback(async () => {
    if (!isAuthenticated || !user) return;

    try {
      setLoading(true);
      setError(null);
      const profile = await apiClient.getUserProfile();
      setUserProfile(profile);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch user profile';
      setError(errorMessage);
      console.error('Error fetching user profile:', err);
    } finally {
      setLoading(false);
    }
  }, [apiClient, isAuthenticated, user]);

  const createProfile = useCallback(async (profile: CreateUserProfileRequest) => {
    try {
      setLoading(true);
      setError(null);
      const newProfile = await apiClient.createUserProfile(profile);
      setUserProfile(newProfile);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create user profile';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  const refreshProfile = useCallback(async () => {
    await fetchUserProfile();
  }, [fetchUserProfile]);

  // Fetch profile on mount and when authentication changes
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchUserProfile();
    } else {
      setUserProfile(null);
      setError(null);
    }
  }, [fetchUserProfile, isAuthenticated, user]);

  return {
    userProfile,
    loading,
    error,
    createProfile,
    refreshProfile,
  };
}

// ============================================================================
// Creator Profile Hooks
// ============================================================================

export interface UseCreatorProfileReturn {
  creatorProfile: CreatorProfileResponse | null;
  loading: boolean;
  error: string | null;
  createProfile: (profile: CreateCreatorProfileRequest) => Promise<void>;
  updateProfile: (creatorId: string, updates: Partial<CreateCreatorProfileRequest>) => Promise<void>;
  refreshProfile: () => Promise<void>;
}

/**
 * Hook for managing creator profile data
 */
export function useCreatorProfile(): UseCreatorProfileReturn {
  const [creatorProfile, setCreatorProfile] = useState<CreatorProfileResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const apiClient = useCreatorDashboardApiClient();
  const { user, isAuthenticated } = useAuth();

  const fetchCreatorProfile = useCallback(async () => {
    if (!isAuthenticated || !user) return;

    try {
      setLoading(true);
      setError(null);
      const profile = await apiClient.getCreatorProfile();
      setCreatorProfile(profile);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch creator profile';
      setError(errorMessage);
      console.error('Error fetching creator profile:', err);
    } finally {
      setLoading(false);
    }
  }, [apiClient, isAuthenticated, user]);

  const createProfile = useCallback(async (profile: CreateCreatorProfileRequest) => {
    try {
      setLoading(true);
      setError(null);
      const newProfile = await apiClient.createCreatorProfile(profile);
      setCreatorProfile(newProfile);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create creator profile';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  const updateProfile = useCallback(async (creatorId: string, updates: Partial<CreateCreatorProfileRequest>) => {
    try {
      setLoading(true);
      setError(null);
      const updatedProfile = await apiClient.updateCreatorProfile(creatorId, updates);
      setCreatorProfile(updatedProfile);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update creator profile';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  const refreshProfile = useCallback(async () => {
    await fetchCreatorProfile();
  }, [fetchCreatorProfile]);

  // Fetch profile on mount and when authentication changes
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchCreatorProfile();
    } else {
      setCreatorProfile(null);
      setError(null);
    }
  }, [fetchCreatorProfile, isAuthenticated, user]);

  return {
    creatorProfile,
    loading,
    error,
    createProfile,
    updateProfile,
    refreshProfile,
  };
}

// ============================================================================
// Content Plan Hooks
// ============================================================================

export interface UseContentPlansReturn {
  contentPlans: ContentPlanResponse[];
  currentPlan: ContentPlanResponse | null;
  loading: boolean;
  loadingMore: boolean;
  isInitialized: boolean;
  error: string | null;
  hasMore: boolean;
  refreshPlans: () => Promise<void>;
  loadMorePlans: () => Promise<void>;
  getPlan: (planId: string) => Promise<ContentPlanResponse | null>;
  getPlanByWeek: (weekStartDate: string, platform: string) => Promise<ContentPlanResponse | null>;
}

/**
 * Hook for managing content plans data
 */
export function useContentPlans(initialLimit: number = 3): UseContentPlansReturn {
  const [contentPlans, setContentPlans] = useState<ContentPlanResponse[]>([]);
  const [allPlans, setAllPlans] = useState<ContentPlanResponse[]>([]); // Store all fetched plans
  const [currentPlan, setCurrentPlan] = useState<ContentPlanResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);

  const apiClient = useCreatorDashboardApiClient();
  const { user, isAuthenticated } = useAuth();

  // Helper to fetch full details for a list of plans
  const hydratePlans = useCallback(async (plans: ContentPlanResponse[]): Promise<ContentPlanResponse[]> => {
    if (plans.length === 0) return [];

    // Fetch details in parallel
    const detailedPlans = await Promise.all(
      plans.map(async (plan) => {
        try {
          // If we already have recommendations, don't re-fetch
          if (plan.recommendations && plan.recommendations.length > 0) return plan;
          return await apiClient.getContentPlan(plan.planId);
        } catch (e) {
          console.warn(`Failed to hydrate plan ${plan.planId}`, e);
          return plan; // Return summary if detail fetch fails
        }
      })
    );
    return detailedPlans;
  }, [apiClient]);

  const fetchContentPlans = useCallback(async (isRefresh = false) => {
    if (!isAuthenticated || !user) return;

    try {
      setLoading(true);
      setError(null);

      // Get current plan with recommendations
      const current = await apiClient.getCurrentContentPlan();
      setCurrentPlan(current);

      // Fetch ALL plans because API 'limit' parameter is unreliable
      const plans = await apiClient.getContentPlans(); // No args = fetch all

      // Sort plans by date descending
      const sortedPlans = plans.sort((a, b) =>
        new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
      );

      setAllPlans(sortedPlans);

      // Initial slice
      let initialSlice = sortedPlans.slice(0, initialLimit);

      // Hydrate the slice with full details (recommendations)
      initialSlice = await hydratePlans(initialSlice);

      // If we have a fresh current plan, ensure it replaces the one in the list
      if (current) {
        initialSlice = initialSlice.map(p => p.planId === current.planId ? current : p);
      }

      setContentPlans(initialSlice);
      setOffset(initialSlice.length);
      setHasMore(initialSlice.length < sortedPlans.length);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch content plans';
      setError(errorMessage);
      console.error('Error fetching content plans:', err);
    } finally {
      setLoading(false);
      setIsInitialized(true);
    }
  }, [apiClient, isAuthenticated, user, initialLimit, hydratePlans]);

  const loadMorePlans = useCallback(async () => {
    if (!isAuthenticated || !user || loadingMore || !hasMore) return;

    try {
      setLoadingMore(true);

      // Simulate network delay for better UX (optional, but requested in requirements to show spinner)
      await new Promise(resolve => setTimeout(resolve, 500));

      let nextSlice = allPlans.slice(offset, offset + initialLimit);

      if (nextSlice.length > 0) {
        // Hydrate the new slice
        nextSlice = await hydratePlans(nextSlice);

        setContentPlans(prev => {
          // De-duplicate just in case
          const existingIds = new Set(prev.map(p => p.planId));
          // Filter out any that we might have already (unlikely with slice but good safety)
          const newUniqueParams = nextSlice.filter(p => !existingIds.has(p.planId));
          return [...prev, ...newUniqueParams];
        });
        setOffset(prev => prev + nextSlice.length);
      }

      if (offset + nextSlice.length >= allPlans.length) {
        setHasMore(false);
      }
    } catch (err) {
      console.error('Error loading more plans:', err);
      // Don't set main error, just log it to avoid disrupting UI
    } finally {
      setLoadingMore(false);
    }
  }, [isAuthenticated, user, allPlans, offset, hasMore, loadingMore, initialLimit, hydratePlans]);

  const getPlan = useCallback(async (planId: string): Promise<ContentPlanResponse | null> => {
    try {
      return await apiClient.getContentPlan(planId);
    } catch (err) {
      console.error('Error fetching content plan:', err);
      return null;
    }
  }, [apiClient]);

  const getPlanByWeek = useCallback(async (weekStartDate: string, platform: string): Promise<ContentPlanResponse | null> => {
    try {
      return await apiClient.getContentPlanByWeek(weekStartDate, platform);
    } catch (err) {
      console.error('Error fetching content plan by week:', err);
      return null;
    }
  }, [apiClient]);

  const refreshPlans = useCallback(async () => {
    // Clear cache before fetching to ensure fresh data
    apiClient.clearCacheEntry('/v1/content-plans');
    setOffset(0); // Reset offset
    setHasMore(true); // Reset hasMore
    await fetchContentPlans(true);
  }, [apiClient, fetchContentPlans]);

  // Fetch plans on mount and when authentication changes
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchContentPlans();
    } else {
      setContentPlans([]);
      setCurrentPlan(null);
      setError(null);
      setIsInitialized(false);
    }
  }, [fetchContentPlans, isAuthenticated, user]);

  return {
    contentPlans,
    currentPlan,
    loading,
    loadingMore,
    isInitialized,
    error,
    hasMore,
    refreshPlans,
    loadMorePlans,
    getPlan,
    getPlanByWeek,
  };
}

// ============================================================================
// Content Recommendation Hooks
// ============================================================================

export interface UseRecommendationActionsReturn {
  updateStatus: (recommendationId: string, statusUpdate: UpdateRecommendationStatusRequest) => Promise<void>;
  updateHelpfulness: (recommendationId: string, helpfulnessRating: 'helpful' | 'not_helpful' | null) => Promise<void>;
  updatePerformance: (recommendationId: string, performanceRating: 'better' | 'average' | 'worse' | null) => Promise<void>;
  loading: boolean;
  error: string | null;
}

/**
 * Hook for managing content recommendation actions
 */
export function useRecommendationActions(): UseRecommendationActionsReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const apiClient = useCreatorDashboardApiClient();

  const updateStatus = useCallback(async (
    recommendationId: string,
    statusUpdate: UpdateRecommendationStatusRequest
  ) => {
    try {
      setLoading(true);
      setError(null);
      await apiClient.updateRecommendationStatus(recommendationId, statusUpdate);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update recommendation status';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  const updateHelpfulness = useCallback(async (
    recommendationId: string,
    helpfulnessRating: 'helpful' | 'not_helpful' | null
  ) => {
    try {
      setLoading(true);
      setError(null);
      await apiClient.updateRecommendationHelpfulness(recommendationId, helpfulnessRating);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update helpfulness rating';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  const updatePerformance = useCallback(async (
    recommendationId: string,
    performanceRating: 'better' | 'average' | 'worse' | null
  ) => {
    try {
      setLoading(true);
      setError(null);
      await apiClient.updateRecommendationPerformance(recommendationId, performanceRating);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update performance rating';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  return {
    updateStatus,
    updateHelpfulness,
    updatePerformance,
    loading,
    error,
  };
}

// ============================================================================
// Combined Dashboard Hook
// ============================================================================

export interface UseDashboardDataReturn {
  userProfile: UserProfileResponse | null;
  creatorProfile: CreatorProfileResponse | null;
  contentPlans: ContentPlanResponse[];
  currentPlan: ContentPlanResponse | null;
  loading: boolean;
  isInitialized: boolean;
  error: string | null;
  refreshAll: () => Promise<void>;
  loadMorePlans: () => Promise<void>;
  hasMorePlans: boolean;
  loadingMorePlans: boolean;
  updateRecommendationStatus: (recommendationId: string, statusUpdate: UpdateRecommendationStatusRequest) => Promise<void>;
  getPlanByWeek: (weekStartDate: string, platform: string) => Promise<ContentPlanResponse | null>;
  apiClient: CreatorDashboardApiClient;
}

/**
 * Combined hook for all dashboard data
 * Provides a single interface for the main dashboard components
 */
export function useDashboardData(): UseDashboardDataReturn {
  const apiClient = useCreatorDashboardApiClient();
  const userProfileHook = useUserProfile();
  const creatorProfileHook = useCreatorProfile();
  const contentPlansHook = useContentPlans();
  const recommendationActionsHook = useRecommendationActions();

  const loading = userProfileHook.loading || creatorProfileHook.loading || contentPlansHook.loading;
  const error = userProfileHook.error || creatorProfileHook.error || contentPlansHook.error || recommendationActionsHook.error;

  const refreshAll = useCallback(async () => {
    // Clear API client cache to ensure fresh data
    apiClient.clearCache();

    await Promise.all([
      userProfileHook.refreshProfile(),
      creatorProfileHook.refreshProfile(),
      contentPlansHook.refreshPlans(),
    ]);
  }, [apiClient, userProfileHook.refreshProfile, creatorProfileHook.refreshProfile, contentPlansHook.refreshPlans]);

  return {
    userProfile: userProfileHook.userProfile,
    creatorProfile: creatorProfileHook.creatorProfile,
    contentPlans: contentPlansHook.contentPlans,
    currentPlan: contentPlansHook.currentPlan,
    loading,
    isInitialized: contentPlansHook.isInitialized,
    error,
    refreshAll,
    loadMorePlans: contentPlansHook.loadMorePlans,
    hasMorePlans: contentPlansHook.hasMore,
    loadingMorePlans: contentPlansHook.loadingMore,
    updateRecommendationStatus: recommendationActionsHook.updateStatus,
    getPlanByWeek: contentPlansHook.getPlanByWeek,
    apiClient,
  };
}